import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import { fmtFCFA, fmtDate, statusBadgeClass } from "../lib/format";
import { Loader2, TrendingUp, Scale, ShieldCheck, Download, FileText, CheckCircle2, Circle, Clock, Sun, Moon } from "lucide-react";
import { Button } from "../components/ui/button";
import { useTheme } from "../context/ThemeContext";

const MS_STATUS = {
  pending: { label: "Pending", icon: Circle, dot: "bg-slate-300", text: "text-slate-500" },
  in_progress: { label: "In Progress", icon: Clock, dot: "bg-blue-500", text: "text-blue-600" },
  done: { label: "Done", icon: CheckCircle2, dot: "bg-emerald-500", text: "text-emerald-600" },
};

const LOGO = "https://static.prod-images.emergentagent.com/jobs/ec99e417-b4fc-4ffa-8f63-7f2d00809cac/images/bd73cb75e48b429917052652638a8324a647ba227ffe71085c2ef15956d66db8.jpeg";
const CATEGORY_IMAGES = {
  "Solar Energy": "https://images.unsplash.com/photo-1668097613572-40b7c11c8727?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "Domestic Electrical": "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "Commercial Electrical": "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "Industrial Electrical": "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "Hospital Equipment": "https://images.unsplash.com/photo-1512102438733-bfa4ed29aef7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "General Supplies": "https://images.unsplash.com/photo-1721937127582-ed331de95a04?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};
const heroFor = (cat) => CATEGORY_IMAGES[cat] || CATEGORY_IMAGES["Industrial Electrical"];

export default function Portal() {
  const { token } = useParams();
  const { data, isError } = useQuery({
    queryKey: ["portal", token],
    queryFn: () => axios.get(`${API}/public/project/${token}`).then((r) => r.data),
    retry: false,
  });
  const error = isError ? "This project link is invalid or has expired." : "";
  const { theme, toggleTheme } = useTheme();
  if (error) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-10 text-center max-w-md">
          <div className="font-heading text-xl font-bold text-slate-900">Link not available</div>
          <p className="text-sm text-slate-500 mt-2">{error}</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-[#0B132B] flex items-center justify-center text-slate-300"><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Loading…</div>;
  }

  const fin = data.financials || {};
  const brand = data.brand || {};
  const accent = brand.accent_color || "#0B132B";
  const barColor = brand.accent_color || "#10b981";
  const logo = brand.logo_url || LOGO;
  const brandName = brand.name || "SilasTech Enterprise";

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <button
        onClick={toggleTheme}
        data-testid="portal-theme-toggle"
        aria-label="Toggle dark mode"
        title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        className="fixed top-4 right-4 z-50 h-10 w-10 rounded-full flex items-center justify-center bg-white border border-slate-200 shadow-sm text-slate-600 hover:text-slate-900 transition-colors"
      >
        {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
      </button>
      <header className="text-white" style={{ backgroundColor: accent }}>
        <div className={`max-w-4xl mx-auto px-6 py-5 flex items-center gap-3 ${brand.logo_position === "center" ? "flex-col text-center" : "justify-between"}`}>
          <div className={`flex items-center gap-3 ${brand.logo_position === "center" ? "flex-col" : ""}`}>
            <img src={logo} alt={brandName} className="h-10 w-10 rounded-lg object-cover bg-white/10" />
            <div className={brand.logo_position === "center" ? "text-center" : ""}>
              <div className="font-heading font-bold" data-testid="portal-brand-name">{brandName}</div>
              <div className={`text-[11px] tracking-wide text-sky-300 ${brand.tagline ? "" : "uppercase"}`}>{brand.tagline || "Client Project Portal"}</div>
            </div>
          </div>
          {brand.logo_position !== "center" && <span className="hidden sm:flex items-center gap-1.5 text-xs text-slate-300"><ShieldCheck className="h-4 w-4" /> Read-only view</span>}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="rounded-xl overflow-hidden border border-slate-200 mb-6 relative h-48" data-testid="portal-hero">
          <img src={heroFor(data.category)} alt={data.category} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute bottom-4 left-5 text-white">
            <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: brand.accent_color ? "#fff" : undefined }}>{data.category}</div>
            <div className="font-heading text-2xl font-bold">{data.name}</div>
          </div>
          {brand.accent_color && <div className="absolute top-0 left-0 h-1 w-full" style={{ backgroundColor: brand.accent_color }} />}
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-900">{data.name}</h1>
              <p className="text-sm text-slate-500 mt-1">{data.category} · {data.location || "—"}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${statusBadgeClass(data.status)}`}>{data.status}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 text-sm">
            <Cell label="Owner" value={data.owner} />
            <Cell label="Phone" value={data.client_phone} />
            <Cell label="Contract Value" value={fmtFCFA(data.total_cost)} />
            <Cell label="Start" value={fmtDate(data.start_date)} />
            <Cell label="Expected End" value={fmtDate(data.end_date)} />
          </div>
          {data.activities?.length > 0 ? (
            <div className="mt-4 border-t border-slate-100 pt-4">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Activities</div>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-600" data-testid="portal-activities">
                {data.activities.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          ) : (data.description && <p className="mt-4 text-sm text-slate-600 border-t border-slate-100 pt-4">{data.description}</p>)}
        </div>

        {data.milestones && data.milestones.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm mt-6 p-6" data-testid="portal-timeline">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-heading text-lg font-semibold text-slate-900">Project Timeline</h2>
              {(() => {
                const t = data.milestones.length;
                const d = data.milestones.filter((m) => m.status === "done").length;
                const ip = data.milestones.filter((m) => m.status === "in_progress").length;
                const pct = t ? Math.round(((d + ip * 0.5) / t) * 100) : 0;
                return <span className="text-sm font-semibold" style={{ color: barColor }} data-testid="portal-progress-pct">{pct}% complete</span>;
              })()}
            </div>
            {(() => {
              const t = data.milestones.length;
              const d = data.milestones.filter((m) => m.status === "done").length;
              const ip = data.milestones.filter((m) => m.status === "in_progress").length;
              const pct = t ? Math.round(((d + ip * 0.5) / t) * 100) : 0;
              return (
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-5" data-testid="portal-progress-bar">
                  <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: barColor }} />
                </div>
              );
            })()}
            <ol className="relative border-l-2 border-slate-100 ml-2">
              {data.milestones.map((m, idx) => {
                const s = MS_STATUS[m.status] || MS_STATUS.pending;
                return (
                  <li key={idx} className="ml-5 pb-5 last:pb-0" data-testid={`portal-milestone-${idx}`}>
                    <span className={`absolute -left-[7px] h-3 w-3 rounded-full ring-4 ring-white ${s.dot}`} />
                    <div className="flex items-center gap-2">
                      <s.icon className={`h-4 w-4 ${s.text}`} />
                      <span className="font-medium text-slate-800">{m.title}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 text-xs">
                      <span className={`font-semibold ${s.text}`}>{s.label}</span>
                      {m.date && <span className="text-slate-400">· {fmtDate(m.date)}</span>}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
          <Stat icon={TrendingUp} label="Total Received" value={fmtFCFA(fin.total_income)} color="text-emerald-700" />
          <Stat icon={Scale} label="Balance Due" value={fmtFCFA(fin.balance)} color="text-slate-800" />
        </div>
        <p className="text-xs text-slate-400 mt-2" data-testid="portal-balance-note">Balance Due is your contract value minus the payments received so far. Internal project costs are not shown here.</p>
        {data.total_cost > 0 && (() => {
          const pct = Math.min(100, Math.round(((fin.total_income || 0) / data.total_cost) * 100));
          const paid = (fin.balance || 0) <= 0;
          return (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm mt-4 p-5" data-testid="portal-payment-progress">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-slate-700">Payment progress</span>
                {paid
                  ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="portal-fully-paid"><CheckCircle2 className="h-3.5 w-3.5" /> Fully paid</span>
                  : <span className="text-sm font-semibold" style={{ color: barColor }} data-testid="portal-payment-pct">{pct}% paid</span>}
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: paid ? "#10b981" : barColor }} />
              </div>
            </div>
          );
        })()}

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm mt-6 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <span className="text-sm font-semibold text-slate-700">Payments Received</span>
            <a href={`${API}/export/public/${token}/invoice.pdf`} target="_blank" rel="noreferrer" data-testid="portal-invoice">
              <Button variant="outline" size="sm" style={{ borderColor: barColor, color: barColor }}><FileText className="h-4 w-4 mr-2" /> Download Statement</Button>
            </a>
          </div>
          {data.income.length === 0 ? <div className="px-5 py-8 text-center text-sm text-slate-400">No payments recorded yet.</div> : (
            <div className="divide-y divide-slate-100">
              {data.income.map((i, idx) => (
                <div key={idx} className="flex items-center justify-between px-5 py-3">
                  <div className="text-sm text-slate-600">{fmtDate(i.date)} · {i.method}</div>
                  <div className="flex items-center gap-3">
                    {i.receipt_file_id && (
                      <a href={`${API}/public/project/${token}/file/${i.receipt_file_id}`} target="_blank" rel="noreferrer"
                        className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1" data-testid={`portal-receipt-${idx}`}>
                        <Download className="h-3.5 w-3.5" /> Receipt
                      </a>
                    )}
                    <div className="font-mono-num font-medium text-emerald-700">{fmtFCFA(i.amount)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="text-center text-xs text-slate-400 mt-8">Powered by STEMS · SilasTech Enterprise Management System</p>
      </main>
    </div>
  );
}
const Cell = ({ label, value }) => <div><div className="text-xs text-slate-400">{label}</div><div className="font-medium text-slate-800">{value || "—"}</div></div>;
const Stat = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
    <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-400"><Icon className="h-4 w-4" /> {label}</div>
    <div className={`mt-2 text-xl font-extrabold font-mono-num ${color}`}>{value}</div>
  </div>
);
